import { Injectable } from '@angular/core';
import * as _ from 'lodash-es';
import { AppService } from '../app.service';
import { GlobalService } from '../global/services/global.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../shared/_services/gtw-snackbar.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { TabGridLoaderService } from '../core/grid/services/tab-grid-loader.service';

@Injectable({
  providedIn: 'root',
})
export class WorkspaceService {
  screens: BehaviorSubject<any> = new BehaviorSubject([]);
  setTabActive: BehaviorSubject<any> = new BehaviorSubject({});
  dataFetched: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isCeScreen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  downloadExcel: Subject<void> = new Subject<void>();
  downloadExcelDone: Subject<void> = new Subject<void>();
  refreshGridData: Subject<any> = new Subject<any>();
  gridLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  hideDataFilters: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  workspaceFactory: any = {
    activeScreen: {},
    activity: {},
    screens: [],
    customDataCache: [],
    lastActiveScreen: [],
    DataCache: {},
    goback: {},
    compTitle: {},
    positionY: [],
    stateId: {},
    routes: {},
    checkTypeLink: ['comment', 'link'],
    workflowObj: false,
  };
  durationInSeconds = 5;

  /**
   * Cross-activity backup of FilterObj instances per screen_key. Survives
   * the `workspaceFactory.screens = _activity.screens` reassignment in
   * `setActivity` — dev/AngularJS parity: there the single mutable
   * `workspaceFactory` holds the same screen references across tabs, so
   * `screen.filters` naturally lives through navigation. In Angular 12
   * each `setActivity` can bring fresh screen refs from USER_MENU; without
   * this cache, previously-loaded FilterObj selections disappear on return.
   */
  private filterObjCache = new Map<string, any>();

  constructor(
    private appService: AppService,
    private globalService: GlobalService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private tabGridLoader: TabGridLoaderService
  ) {}

  setCompName(name: string): void {
    this.workspaceFactory.compTitle = name;
  }

  setPositionY(posY: any): void {
    this.workspaceFactory.positionY = posY;
  }

  setParamsId(id: any): void {
    this.workspaceFactory.stateId = id;
  }

  setActivity(_activity: any, _changeState: any, _screen: any): void {
    // Snapshot previous state so we can migrate FilterObj / data / cache
    // flags onto the new screen references. Dev/AngularJS parity: there
    // `screens` is a stable mutable array in the factory; Angular 12 can
    // receive fresh refs from USER_MENU traversal on every setActivity,
    // which would otherwise drop user's filter selections and loaded
    // grid payloads on every drill-down return.
    const prevScreens: any[] = Array.isArray(this.workspaceFactory.screens)
      ? this.workspaceFactory.screens
      : [];

    this.workspaceFactory.activeScreen = {};
    this.workspaceFactory.activity = {};
    this.workspaceFactory.screens = [];

    // Defensive: if `_activity` came from a search-filtered clone, its
    // `screens` array may be trimmed to only the matched screen(s). Legacy
    // `workspaceFactory.setActivity` always received the live USER_MENU
    // activity, so the tab bar showed every sibling screen. Re-hydrate the
    // canonical activity from USER_MENU when we have an `activity_key` and
    // the incoming activity is a filtered clone (different reference) — this
    // keeps the full `screens` list intact for the tab strip.
    let resolvedActivity = _activity;
    if (_activity?.activity_key) {
      const canonical = this.findActivity(_activity.activity_key);
      if (
        canonical &&
        canonical !== _activity &&
        Array.isArray(canonical.screens) &&
        (!Array.isArray(_activity.screens) ||
          canonical.screens.length > _activity.screens.length)
      ) {
        resolvedActivity = canonical;
        if (_screen?.screen_key) {
          const canonicalScreen = this.findScreen(canonical, _screen.screen_key);
          if (canonicalScreen) {
            _screen = canonicalScreen;
          }
        }
      }
    }

    this.workspaceFactory.activity = resolvedActivity;
    this.workspaceFactory.screens = resolvedActivity.screens;
    _activity = resolvedActivity;

    if (Array.isArray(this.workspaceFactory.screens)) {
      this.workspaceFactory.screens.forEach((newScreen: any) => {
        if (!newScreen?.screen_key) return;
        const prev = prevScreens.find(
          (p: any) => p && String(p.screen_key) === String(newScreen.screen_key)
        );
        if (prev && prev !== newScreen) {
          if (newScreen.filters == null && prev.filters != null) {
            newScreen.filters = prev.filters;
          }
          if (newScreen.data == null && prev.data != null) {
            newScreen.data = prev.data;
          }
          if (!newScreen.cachedData && prev.cachedData) {
            newScreen.cachedData = prev.cachedData;
          }
        }
        if (newScreen.filters == null) {
          const cachedFilters = this.filterObjCache.get(String(newScreen.screen_key));
          if (cachedFilters) {
            newScreen.filters = cachedFilters;
          }
        }
        if (newScreen.data == null) {
          const cachedData = this.tabGridLoader?.getCached?.(newScreen.screen_key);
          if (cachedData) {
            newScreen.data = cachedData;
            newScreen.cachedData = true;
          }
        }
      });
    }

    this.screens.next(this.workspaceFactory.screens);

    const findLastActive = _.find(this.workspaceFactory.lastActiveScreen, {
      activity_key: _activity.activity_key,
    });

    if (_screen !== undefined && _screen !== null) {
      // `_screen` came from `findScreen(activity, screen_key)`, so it's already
      // an element of `_activity.screens` — same reference as the freshly
      // hydrated entry above. Safe to assign directly.
      this.workspaceFactory.activeScreen = _screen;
    } else if (findLastActive) {
      const index = _.findIndex(_activity.screens, (s: any) =>
        String(s.screen_key) === String(findLastActive.screen_key)
      );
      this.workspaceFactory.activeScreen =
        index >= 0 ? _activity.screens[index] : _activity.screens[0];
    } else {
      if (!_activity.screens && _activity.children) {
        _activity.screens = _activity.children;
      }
      this.workspaceFactory.activeScreen = _activity.screens[0];
    }

    if (_changeState !== false) {
      this.loadTab(this.workspaceFactory.activeScreen, {}, {});
    } else {
      this.globalService.setFilters(this.workspaceFactory.activeScreen);
      this.setTabActive.next(this.workspaceFactory.activeScreen);
    }
  }

  /**
   * Save a FilterObj snapshot for a screen so that a later setActivity call
   * can restore it even if USER_MENU traversal produces a fresh screen
   * reference. Called from DataFiltersComponent once a FilterObj is loaded.
   */
  cacheFilterObj(screen: any): void {
    if (!screen?.screen_key || !screen?.filters?.loaded) return;
    this.filterObjCache.set(String(screen.screen_key), screen.filters);
  }

  loadTab(workspace: any, _gridFilters: any, onBackbutton: any): void {
    this.setBackButton(workspace, _gridFilters);

    const findLastActive = _.find(this.workspaceFactory.lastActiveScreen, {
      activity_key: workspace.activity_key,
    });

    if (findLastActive) {
      findLastActive.screen_key = workspace.id;
    } else {
      this.workspaceFactory.lastActiveScreen.push({
        activity_key: workspace.activity_key,
        screen_key: workspace.id,
      });
    }

    // Defensive: ensure `activeScreen` is the same object reference as the
    // matching element in `workspaceFactory.screens`. Callers (e.g. click
    // on a tab surfaced via `WorkspaceComponent.workspaces`) sometimes pass
    // a stale copy from a previous `screens.next` snapshot; without this
    // re-resolve, DataFilters mutations like `activeScreen.filters = ...`
    // land on the detached copy and don't show up in the tab strip state.
    const screens = this.workspaceFactory.screens;
    const matched = Array.isArray(screens)
      ? _.find(screens, (s: any) =>
          s && workspace && String(s.screen_key) === String(workspace.screen_key)
        )
      : null;
    const activeScreen = matched || workspace;

    this.globalService.setFilters(activeScreen);
    this.workspaceFactory.activeScreen = activeScreen;

    if (!onBackbutton || _.isEmpty(onBackbutton)) {
      this.screens.next(this.workspaceFactory.screens);
      this.setTabActive.next(activeScreen);
    }
  }

  setBackButton(workspace: any, _gridFilters: any): void {
    const backScreens = ['10246', '10191', '10233', '10906'];
    if (
      backScreens.includes(workspace.screen_key) ||
      (workspace.parent && workspace.parent.activity_key == '544')
    ) {
      this.workspaceFactory.goback = {
        type: 'standard',
        workspace,
        gridFilters: _gridFilters,
        backMessage: 'Back to ' + workspace.label,
      };
    }
  }

  findActivity(_activity_key: any): any {
    let activity: any = null;
    function search(value2: any): void {
      if (value2.type === 'activity' && value2.activity_key == _activity_key) {
        activity = value2;
        return;
      }
      if (value2.children) {
        _.forEach(value2.children, (_value: any) => search(_value));
      }
    }
    _.forEach(this.appService.USER_MENU, (value: any) => {
      if (value.children) { search(value); }
      return false;
    });
    return activity;
  }

  findScreen(_activity: any, _screen_key: any): any {
    if (!_activity?.screens?.length) {
      return undefined;
    }
    // USER_MENU JSON may use numeric screen_key; drill payloads often use strings.
    return _.find(_activity.screens, (s: any) =>
      s != null && String(s.screen_key) === String(_screen_key)
    );
  }

  getInheritedFilters(screen: any): any {
    if (!screen || Number(screen.filter_inherit) !== 1 || !screen.filter_key) {
      return null;
    }

    const siblings = screen.parent?.screens || this.workspaceFactory.screens || [];
    const sibling = siblings.find(
      (candidate: any) => candidate !== screen
        && candidate.filters?.loaded
        && candidate.filter_key == screen.filter_key
    );

    return sibling?.filters || null;
  }

  createLinkFilters(): any {
    return this.workspaceFactory.activeScreen.filters
      ? this.workspaceFactory.activeScreen.filters.getFilterParamsFromCache()
      : {};
  }

  loadLink(
    _type: any, _comment: any, _activity_key: any, _screen_key: any,
    _filters?: any, _table_state?: any, _locationObj?: any, _actionGrid?: any, _noReload?: any,
    _drillDownRefresh?: any
  ): void {
    const activity = this.findActivity(_activity_key);
    if (activity) {
      activity.selected = 'selected';
      const screen = this.findScreen(activity, _screen_key);

      if (_type == 'workflow') {
        this.workspaceFactory.workflowObj = true;
      } else {
        this.workspaceFactory.workflowObj = false;
        this.setBackButton(this.workspaceFactory.activeScreen, []);
      }

      if (activity && screen) {
        const forceFilterReload =
          _type === 'drillDown' && _locationObj?.forceFilterReload === true;
        if (forceFilterReload) {
          screen.filters = null;
        }

        // Assign commentLink / actionGrid on the target screen BEFORE setActivity
        // so synchronous subscribers of setTabActive (GridDirective,
        // DataFiltersComponent) observe the drill-down context when they react
        // to the tab change.
        //
        // For `_type === 'drillDown'` we set commentLink even when `_filters`
        // is empty. Some column drilldown configs carry neither
        // `dataObj.filters` nor `dataObj.screenFilter` (e.g. HO/Branch
        // Summary → GAAP Detail) — the only useful context is the source
        // screen's current selections, transferred via the fast-path
        // `screen.filters = sourceScreen.filters` below. Without
        // commentLink, `DataFiltersComponent.isDrillDownLink` returns false
        // → `dataFetched` stays false → `GridDirective.initGrid` bails on
        // first visit after a build and the grid never fires its POST.
        //
        // We deliberately DO NOT extend this to `_type === 'link'`: the
        // back-button (`workspace.component.ts.backToPreviousScr`) calls
        // loadLink('link', …, {}) with empty filters, and setting
        // commentLink on the target screen there would later trick
        // `applyCommentLinkFilters` into clobbering fresh selections
        // (e.g. cascade after a tax_year change) with stale empty values.
        if (!_.isEmpty(_filters) || _type === 'drillDown') {
          screen.commentLink = {
            type: _type,
            filters: _filters || [],
            table_state: _table_state,
            locationObj: _locationObj,
            action_template_key: _actionGrid,
            forceFilterReload,
            // Legacy parity (gridController.js:675-681): when the source
            // column had `drilldown_filter_refresh=1`, row-level predicates
            // from `table_state` must be injected into the target grid POST
            // body (as lowercase keys) instead of the usual global/filter
            // params. Captured here so `GridDirective.loadGridData` can
            // read it synchronously when the target tab starts fetching.
            drillDownRefresh: Number(_drillDownRefresh) === 1 ? 1 : 0,
          };
          screen.actionGrid = _actionGrid || null;
        }

        // Drill-down / link parity: transfer the source tab's filter
        // selections to the target. Legacy AngularJS used a single shared
        // GlobalService.globalParams, so ME / year / scenario etc. were
        // always available on the target. In Angular each screen owns its
        // own FilterObj. Two strategies:
        //
        // 1. Same filter_key → share the FilterObj reference (fast path,
        //    target inherits source selections immediately).
        // 2. Different filter_key → enrich _filters with the source
        //    screen's current filter params so applyCommentLinkFilters can
        //    apply matching values to the target's own filter controls.
        if ((_type === 'drillDown' || _type === 'link') && screen !== this.workspaceFactory.activeScreen) {
          const sourceScreen = this.workspaceFactory.activeScreen;
          if (
            !forceFilterReload &&
            sourceScreen?.filters?.loaded &&
            sourceScreen.filter_key &&
            screen.filter_key &&
            sourceScreen.filter_key == screen.filter_key
          ) {
            screen.filters = sourceScreen.filters;
          } else if (sourceScreen?.filters?.loaded && Array.isArray(_filters)) {
            const srcFilters = sourceScreen.filters.getFilters?.() || [];
            const srcParams = sourceScreen.filters.getFilterParams?.() || {};
            console.log('[loadLink] enriching _filters from source screen, srcFilters count:', srcFilters.length, 'srcParams keys:', Object.keys(srcParams));
            for (const sf of srcFilters) {
              if (!sf?.param_name) continue;
              const paramValue = srcParams[sf.param_name];
              if (paramValue == null || String(paramValue) === '') continue;
              const alreadyPresent = _filters.some((f: any) =>
                (f.filter_key && sf.filter_key && String(f.filter_key) === String(sf.filter_key)) ||
                (f.name && String(f.name).toLowerCase() === String(sf.param_name).toLowerCase())
              );
              if (!alreadyPresent) {
                console.log('[loadLink] adding source filter:', sf.param_name, 'filter_key:', sf.filter_key, 'value len:', String(paramValue).length);
                _filters.push({
                  filter_key: sf.filter_key,
                  name: sf.param_name,
                  value: paramValue,
                });
              }
            }
          }
        }

        this.setActivity(activity, true, screen);
      }
    }
  }

  setCustomDataCache(_data_key: any, _data: any): boolean {
    this.workspaceFactory.customDataCache.push({ data_key: _data_key, data: _data });
    return true;
  }

  getCustomDataCache(data_key: any): any {
    return _.find(this.workspaceFactory.customDataCache, { data_key });
  }

  setDataCache(_data_key: any, _data: any): void {
    this.workspaceFactory.DataCache[_data_key] = _data;
  }

  getDataCache(data_key: any): any {
    return this.workspaceFactory.DataCache[data_key];
  }

  getScreen(): any {
    return this.workspaceFactory.screens;
  }

  findNode(collection: any, value: any, predicate: any, children: any): any {
    let node: any = null;
    function findNodeSearch(coll: any, val: any, pred: any, ch: any) {
      for (let i = 0; i < coll.length; i++) {
        const n = coll[i][pred];
        if (n && _.includes(n.toLowerCase(), val.toLowerCase())) {
          node = coll[i];
          break;
        }
        if (coll[i][ch]?.length) {
          findNodeSearch(coll[i][ch], val, pred, ch);
        }
      }
    }
    findNodeSearch(collection, value, predicate, children);
    return node;
  }

  onLoad(): void {
    const defaultActivity = this.findNode(
      this.appService.USER_MENU || [],
      'Y',
      'default_activity',
      'children'
    );

    if (defaultActivity) {
      defaultActivity.selected = 'selected';
      this.setActivity(defaultActivity, true, null);
    }
  }

  setCache(workspace: any, data: any): void {
    if (!workspace) return;
    // Delegate so the singleton cache map (keyed by `screen_key`) is
    // populated too — that's what `WorkspaceComponent.hydrateTabDotStates`
    // reads on workspace re-mount to re-light the orange dot for tabs
    // that already have data, even when the screen objects themselves are
    // brand new instances.
    this.tabGridLoader.setCache(workspace, data);
  }

  checkFilters(workspace: any): void {
    // Ensures filters are set on the workspace screen
    if (workspace && !workspace.filters) {
      workspace.filters = { loaded: false };
    }
  }

  setCurrentFilters(workspace: any): void {
    this.checkFilters(workspace);
  }

  clearCache(workspace: any): void {
    if (!workspace) return;
    // Cancel any in-flight grid fetch for this screen so the very next
    // refresh actually issues a fresh request. Without this, a pending
    // request from a previous user interaction (e.g. an earlier filter
    // click whose response is still on the wire) would make `fetchForScreen`
    // bail early on the next refresh — the user would change tax_year,
    // hit "Filter", and see no network call at all.
    this.tabGridLoader.cancelInFlight(workspace.screen_key);
    // Delegate to the loader so the entry is also evicted from the
    // singleton `cache` map keyed by `screen_key`. Setting
    // `workspace.cachedData = null` only on the local screen reference is
    // not enough: after a workspace re-mount, the prior reference is gone
    // but the cache map would still serve its stale payload on the next
    // `getCached(screen_key)` call — making the "Filter" button appear to
    // do nothing (no HTTP, just a re-render of the prior data).
    this.tabGridLoader.clearCache(workspace);
  }

  getScreenSlug(screen: any): string {
    const link = screen?.link || screen?.SCREEN_LINK || '';
    if (link && link.startsWith('workspace.')) {
      return link.substring('workspace.'.length);
    }
    if (link) {
      return link;
    }
    return screen?.screen_key || screen?.SCREEN_KEY || '';
  }

  /**
   * Router commands for the URL a workspace tab should drive the address
   * bar to when activated. Default is `/workspace/<slug>`, but a small set
   * of legacy uib-router parents are registered top-level (NOT under
   * `workspace.` — see `ui/app/components/formView/tabWrapper/formViewTab.js`)
   * and uib-router translates their dot-state names into slash-separated
   * URLs. For those screens the live URL is `/form-view/dashboard` etc., and
   * `setTab(...)` must mirror that exactly so the URL doesn't flicker
   * through `/workspace/form-view.dashboard` and break deep-link parity.
   *
   * Add a screen here only if its legacy `$stateProvider.state(...)` parent
   * does NOT begin with `workspace.`.
   */
  getScreenNavigationCommands(screen: any): any[] {
    const slug = this.getScreenSlug(screen);
    const custom = WorkspaceService.TOP_LEVEL_LEGACY_STATE_COMMANDS[slug];
    if (custom) return custom.slice();
    return ['/workspace', slug];
  }

  /**
   * String form of `getScreenNavigationCommands` for `router.url` startsWith
   * comparisons in `WorkspaceComponent.setTabActive` (avoids re-navigating
   * when the address bar already matches the target tab).
   */
  getScreenNavigationPath(screen: any): string {
    const commands = this.getScreenNavigationCommands(screen);
    if (!commands.length) return '';
    const joined = commands.join('/').replace(/\/+/g, '/');
    return joined.startsWith('/') ? joined : '/' + joined;
  }

  private static readonly TOP_LEVEL_LEGACY_STATE_COMMANDS: Record<string, any[]> = {
    // formViewTab.js registers `form-view` / `state-form-view` as top-level
    // states (NOT children of `workspace.`), so the live URL is
    // `/form-view/dashboard` and `/state-form-view/dashboard`. The matching
    // direct routes are wired in `app-routing.module.ts`, and the
    // CE-detection path in `WorkspaceScreenComponent` honours these via
    // `route.data.screenLink`.
    'form-view.dashboard': ['/form-view', 'dashboard'],
    'state-form-view.dashboard': ['/state-form-view', 'dashboard'],
    // The Tax Payer / Submissions Dashboard ports legacy `submissions-dasboard.list`
    // (and its submission-detail child `submissions-dasboard.submission`) as
    // TOP-LEVEL routes in `EfileRoutingModule` (`/submissions-dasboard/list` and
    // `/submissions-dasboard/submission/:id`) — they are NOT children of
    // `/workspace/`. Without these mappings any programmatic `setActivity`
    // for the Tax Payer screen would default to `/workspace/<slug>` and
    // bounce the user off the page. `SubmissionDataComponent.ensureWorkspaceContext`
    // calls `setActivity` on init to push the screen's
    // activity_key / screen_key / jcd_ta_key into `globalParams`, and relies
    // on these mappings for the resulting `setTabActive` no-op
    // `router.url.startsWith` check.
    'submissions-dasboard.list': ['/submissions-dasboard', 'list'],
    'submissions-dasboard.submission': ['/submissions-dasboard', 'submission'],
  };

  findScreenByLink(link: string): { activity: any; screen: any } | null {
    let result: { activity: any; screen: any } | null = null;
    const search = (node: any, parentActivity?: any, parentNode?: any) => {
      if (result) return;

      if (parentNode && !node.parent) {
        node.parent = parentNode;
      }

      if (node.type === 'screen' && node.link === link) {
        result = { activity: parentActivity, screen: node };
        return;
      }
      const act = node.type === 'activity' ? node : parentActivity;
      if (node.children?.length) {
        node.children.forEach((child: any) => search(child, act, node));
      }
      if (node.screens?.length) {
        node.screens.forEach((child: any) => search(child, act, node));
      }
    };
    (this.appService.USER_MENU || []).forEach((item: any) => search(item));
    return result;
  }

  /**
   * Finds a screen anywhere in USER_MENU by its numeric screen_key.
   * Mirrors `findScreenByLink` but matches on screen_key instead of link.
   * Used when the URL carries a numeric key (e.g. `/workspace/10100`) and we
   * need to determine the correct activity + breadcrumb context on reload.
   */
  findScreenByKey(screenKey: string): { activity: any; screen: any } | null {
    let result: { activity: any; screen: any } | null = null;
    const key = String(screenKey);
    const search = (node: any, parentActivity?: any) => {
      if (result) return;
      if (node.type === 'screen' && String(node.screen_key) === key) {
        result = { activity: parentActivity, screen: node };
        return;
      }
      const act = node.type === 'activity' ? node : parentActivity;
      if (node.children?.length) {
        node.children.forEach((child: any) => search(child, act));
      }
      if (node.screens?.length) {
        node.screens.forEach((child: any) => search(child, act));
      }
    };
    (this.appService.USER_MENU || []).forEach((item: any) => search(item));
    return result;
  }

  /**
   * Returns the ordered list of ancestor labels (root → screen) for the given
   * screen link by performing a stateless DFS over USER_MENU. Unlike
   * `findScreenByLink`, this method does NOT mutate any node references, so it
   * is safe to call at any time and always reflects the true menu hierarchy
   * regardless of which `findScreenByLink` calls preceded it.
   */
  findBreadcrumbPath(link: string): string[] {
    const result: string[] = [];
    const search = (node: any, ancestors: string[]): boolean => {
      const current = node.label ? [...ancestors, node.label] : [...ancestors];
      if (node.type === 'screen' && node.link === link) {
        result.push(...current);
        return true;
      }
      if (node.children?.length) {
        for (const child of node.children) {
          if (search(child, current)) return true;
        }
      }
      if (node.screens?.length) {
        for (const child of node.screens) {
          if (search(child, current)) return true;
        }
      }
      return false;
    };
    for (const item of (this.appService.USER_MENU || [])) {
      if (search(item, [])) break;
    }
    return result;
  }

  openSnackBar(message: string, panelClass: string = '') {
    this.gtwSnackbar.openSnackBar(message, (panelClass || '') as any, true, this.durationInSeconds);
  }
}
